import React, { useEffect, useCallback, useState, useRef } from 'react';
import { useSetAtom } from 'jotai';
import { userAtom } from './userAtoms';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

interface AuthStateManagerProps {
  children: React.ReactNode;
}

export const AuthStateManager: React.FC<AuthStateManagerProps> = ({ children }) => {
  const setUser = useSetAtom(userAtom);
  const navigate = useNavigate();
  const location = useLocation();
  // Kept in refs so the session check below reads the current route and
  // navigator without re-creating itself per navigation. Written from an effect:
  // a ref write during render leaks out of discarded concurrent renders.
  const locationRef = useRef(location);
  const navigateRef = useRef(navigate);
  useEffect(() => {
    locationRef.current = location;
    navigateRef.current = navigate;
  }, [location, navigate]);
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/session', {
        credentials: 'include',
      });
      if (response.status === 401) {
        setUser(null);
        return;
      }
      if (response.status === 403) {
        setUser(null);
        navigateRef.current('/login', { state: { showAccessDenied: true }, replace: true });
        return;
      }
      // Only 401 and 403 say anything about the session. A 429, a 502 mid-deploy
      // or a dropped connection means unreachable, and signing out over it
      // discards a session the server still holds.
      if (!response.ok) {
        console.error(`Session check failed: ${response.status}`);
        return;
      }
      const data = await response.json();

      if (data.authenticated) {
        setUser(data);

        if (locationRef.current.pathname === '/login') {
          const from = (locationRef.current.state as { from?: Location })?.from?.pathname || '/';
          navigateRef.current(from);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      // A failed request is not a signed-out session either.
      console.error('Error checking session:', error);
    } finally {
      setLoading(false);
    }
    // Via navigateRef: react-router rebuilds `navigate` on every location change,
    // so depending on it re-ran this and re-requested the session per navigation.
  }, [setUser]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    const controller = new AbortController();

    const interval = setInterval(async () => {
      try {
        const response = await fetch('/api/auth/session', {
          credentials: 'include',
          signal: controller.signal,
        });
        if (response.status === 401) {
          setUser(null);
          return;
        }
        if (response.status === 403) {
          setUser(null);
          navigateRef.current('/login', { state: { showAccessDenied: true }, replace: true });
          return;
        }
        if (!response.ok) {
          console.error(`Session check failed: ${response.status}`);
          return;
        }
        const data = await response.json();

        if (!data.authenticated) {
          setUser(null);
        }
      } catch (error) {
        // The unmount cleanup aborts this request. Asked of the signal rather
        // than of the rejection: the DOMException fetch rejects with need not
        // be an instance of the DOMException this module can see.
        if (controller.signal.aborted) {
          return;
        }
        console.error('Error checking session:', error);
      }
    }, 60000);

    return () => {
      controller.abort();
      clearInterval(interval);
    };
  }, [setUser]);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return <>{children}</>;
};
